// ** React
import { useMemo, memo, useRef, useState, useEffect } from "react";

// ** Redux
import { useSelector } from "react-redux";

// ** Third Party Components
import { useTranslation } from "react-i18next";

// ** Components
import Text from "components/typography/Text";
import Media from "components/layout/Media";
import UserAvatar from "components/user/UserAvatar";
import Login from "components/user/Login";
import routes from "containers";
import Block from "components/layout/Block";
import MultiLineText from "components/typography/Text/MultiLineText";
import Likes from "./Likes";
import Followers from "./Followers";
import { CardWrapper } from "components/layout/Card/CardWrapper";
import { CardBody } from "components/layout/Card/CardBody";

// ** Utils
import generatePath from "utils/generatePath";
import { processCoupleSexWithAge } from "utils/processCouple";
import convertText from "utils/convertText";
import DateTime from "utils/datetime";
import sharedObservers from "utils/sharedObservers";
import { getUserStatus } from "utils/userStatus";

// #####################################################

const UserMediaUpgraded = ({
	secondLinePrepend,
	secondLineAppend,
	thirdLinePrepend,
	thirdLineAppend,
	thirdLinePrependX,
	thirdLineAppendX,
	loginProps,
	vipProps,
	avatarProps,
	smallOnMobile,
	maxWidthWithEllipsis,
	user: {
		create,
		user_data = {},
		partner_1,
		partner_2,
		sex,
		city,
		country,
		avatar,
		login,
		status_vip,
		online,
		profile_status,
		private_data,
		privateData,
		likes,
		followers_count,
		followers,
		description,
		active,
		id,
		search_for: searchFor,
	},
}) => {
	const { t } = useTranslation(["common", "countries"]);
	const to = generatePath(routes.user.path, {
		login: login?.toLowerCase(),
	});

	const [inView, setInView] = useState(true);
	const containerRef = useRef(null);
	const lastHeightRef = useRef(null);

	useEffect(() => {
		if (!containerRef.current) return;

		const cleanupIntersection = sharedObservers.observeIntersection(
			containerRef.current,
			(isIntersecting) => {
				if (!isIntersecting && containerRef.current) {
					lastHeightRef.current =
						containerRef.current.offsetHeight ||
						lastHeightRef.current;
				}
				setInView(isIntersecting);
			},
		);

		return () => {
			cleanupIntersection();
		};
	}, [id]);

	const avatarPropsFinal = useMemo(() => {
		return {
			login,
			src: avatar,
			online,
			status: profile_status,
			privateData: private_data || privateData,
			userId: id,
			noOnlineCircle: true,
			...avatarProps,
		};
	}, [avatar, profile_status, private_data, privateData, id, avatarProps]);

	const secondLineText = () => {
		if (sex === "couple") {
			return `${processCoupleSexWithAge({
				sex1: partner_1.sex,
				age1: partner_1.age,
				sex2: partner_2.sex,
				age2: partner_2.age,
				t,
			})}, ${city && `${city}, `}${t(`countries:${country}`)}`;
		}
		return `${t(`sexOptions.${sex}`)}, ${
			user_data.age ? `${user_data.age} ${t("years")},` : ""
		} ${city && `${city}, `}${t(`countries:${country}`)}`;
	};

	const avatarAltLine = () => {
		if (sex === "couple") {
			return `${processCoupleSexWithAge({
				sex1: partner_1.sex,
				age1: partner_1.age,
				sex2: partner_2.sex,
				age2: partner_2.age,
				t,
			})}, ${city && `${city}`}`;
		}
		return `${t(`sexOptions.${sex}`)},${
			user_data.age ? ` ${user_data.age} ${t("years")},` : ""
		} ${city && `${city}`}`;
	};

	const renderSecondLinePrepend = () => {
		return typeof secondLinePrepend === "function" ? (
			secondLinePrepend()
		) : (
			<Text>{secondLinePrepend}</Text>
		);
	};

	const renderSecondLineAppend = () => {
		return typeof secondLineAppend === "function" ? (
			secondLineAppend()
		) : (
			<Text>{secondLineAppend}</Text>
		);
	};

	const searchForArray = Object.keys(searchFor).filter(
		(key) => searchFor[key] === true,
	);

	const formattedLastActivityDate = new DateTime(active).format("D.MM.YYYY");
	const formattedCreateAccountDate = new DateTime(create).format("D.MM.YYYY");

	// ** For dynamic changed online and status for logged user
	const currentLoggedUser = useSelector((state) => state.globalUser);
	const isCurrentLoggedUser = currentLoggedUser.id === id;
	const correctOnline = isCurrentLoggedUser
		? currentLoggedUser.online
		: online;
	const correctStatus = isCurrentLoggedUser
		? currentLoggedUser.profileStatus
		: profile_status;

	// online - 0 ukryty, 1 zielony, 2 zółty,
	// profile status - 1 online , 2 spotkam się , 3 szary,  4 niewidoczny

	const thirdLineTextX = () => {
		const userStatus = getUserStatus({
			status: correctStatus,
			online: correctOnline,
			isMe: isCurrentLoggedUser,
			userLogin: login,
			t,
			formattedLastActivityDate,
		});
		return (
			<Text small color="grey">
				{userStatus}
				{userStatus && ", "}
				{t("common:accountSince").toLowerCase()}:{" "}
				{formattedCreateAccountDate}
				{(likes > 0 || followers_count > 0) && ", "}
				{likes > 0 && (
					<>
						<Likes likes={likes} color="grey" />
						{followers_count > 0 && <>, </>}
					</>
				)}
				{followers_count > 0 && (
					<Followers color="grey" number={followers_count} />
				)}
			</Text>
		);
	};
	const renderThirdLinePrependX = () => {
		return typeof thirdLinePrepend === "function" ? (
			thirdLinePrepend()
		) : (
			<Text>{thirdLinePrepend}</Text>
		);
	};
	const renderThirdLineAppendX = () => {
		return typeof thirdLineAppend === "function" ? (
			thirdLineAppend()
		) : (
			<Text>{thirdLineAppend}</Text>
		);
	};

	const avatarAlt = `${login} - ${avatarAltLine()}`.trim();

	// ############################################################################

	return (
		<CardWrapper
			$hoverInLightMode
			ref={containerRef}
			style={{
				minHeight: inView
					? "auto"
					: lastHeightRef.current > 0
						? lastHeightRef.current
						: "148px", // single line post height
			}}
		>
			{inView && (
				<CardBody $isUsersListingCard>
					<Media
						noXmargins
						to={to}
						scrollForce
						avatarComponent={UserAvatar}
						avatarProps={avatarPropsFinal}
						avatarAlt={avatarAlt}
						smallOnMobile={smallOnMobile}
						followers={followers}
						name={() => (
							<Block pr={followers?.is_allowed ? 8 : 0}>
								<Login
									login={login}
									vip={status_vip}
									size={"mbase"}
									maxWidthWithEllipsis={maxWidthWithEllipsis}
									{...loginProps}
									vipProps={vipProps}
								/>
							</Block>
						)}
						padding={0}
						paddingY={0}
						subname={() => (
							<>
								<Text
									bold
									color="black"
									pr={followers?.is_allowed ? 8 : 0}
								>
									{secondLinePrepend
										? renderSecondLinePrepend()
										: null}
									<Text>{secondLineText()}</Text>
									{secondLineAppend
										? renderSecondLineAppend()
										: null}
								</Text>

								{searchForArray.length > 0 && (
									<Text small bold>
										{searchForArray.map((item, index) => {
											return (
												<>
													{t(
														`filtersAndSelectors:purpose.${item}`,
													)}
													{index <
														searchForArray.length -
															1 && ", "}
												</>
											);
										})}
									</Text>
								)}

								{thirdLinePrependX ? (
									<Text small color="grey">
										{renderThirdLinePrependX()}
									</Text>
								) : null}

								{thirdLineTextX()}

								{thirdLineAppendX ? (
									<Text small color="grey">
										{renderThirdLineAppendX()}
									</Text>
								) : null}
							</>
						)}
						description={() => {
							if (description) {
								return (
									<Block flex fullHeight>
										<MultiLineText
											withShowMoreButton={false}
											text={convertText({
												pieces: description,
											})}
											maxLines={6}
											color="var(--dz-sys-text-color)"
										/>
									</Block>
								);
							}

							return null;
						}}
					/>
				</CardBody>
			)}
		</CardWrapper>
	);
};

// #####################################################

UserMediaUpgraded.defaultProps = {
	smallOnMobile: true,
};

UserMediaUpgraded.displayName = "UserMediaUpgraded";

export default memo(UserMediaUpgraded);

// #####################################################
